/**
 * Opleiders-cockpit — hoofdmenu (top-navigatie) + submenu's (linker zijbalk) + rolmatrix.
 * Eén registry voor Layout.tsx (topnav), OpleiderCockpitLayout (zijbalk/grendel) en App.tsx.
 *
 *   OWNER          alles
 *   ADMINISTRATOR  alles behalve Teambeheer en Finance (wel Finance › Helpdesk & Escalaties)
 *   TRAINER        Trainingen Overzicht + Cursist-Gesprekken (eigen klassen)
 */
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, Radio, Eye, Building2,
  GraduationCap, PlusCircle, Table2, MonitorPlay, UsersRound,
  BarChart3, PieChart, Trophy, Sparkles,
  Euro, Landmark, Receipt, Percent, Scale,
  MessageSquare, Smartphone, HeartHandshake,
} from 'lucide-react';

export type OpleiderRol = 'OWNER' | 'ADMINISTRATOR' | 'TRAINER';
export const ALLE_ROLLEN: OpleiderRol[] = ['OWNER', 'ADMINISTRATOR', 'TRAINER'];
const BEHEER: OpleiderRol[] = ['OWNER', 'ADMINISTRATOR'];
const EIGENAAR: OpleiderRol[] = ['OWNER'];

export interface OpleiderSubItem {
  key: string;
  pad: string;
  icon: LucideIcon;
  rollen: OpleiderRol[];
  /** Extra paden die onder dit submenu vallen (actief-markering). */
  aliassen?: string[];
}

export interface OpleiderHoofdmenu {
  key: 'dashboard' | 'academy' | 'data' | 'finance' | 'chats';
  pad: string;
  icon: LucideIcon;
  rollen: OpleiderRol[];
  items: OpleiderSubItem[];
}

export const OPLEIDER_MENU: OpleiderHoofdmenu[] = [
  {
    key: 'dashboard',
    pad: '/opleider/dashboard',
    icon: LayoutDashboard,
    rollen: BEHEER,
    items: [
      { key: 'signals', pad: '/opleider/dashboard', icon: Radio, rollen: BEHEER },
      { key: 'cursisten', pad: '/opleider/dashboard/cursisten', icon: Eye, rollen: BEHEER },
      { key: 'offertes', pad: '/opleider/dashboard/offerte-aanvragen', icon: Building2, rollen: BEHEER },
    ],
  },
  {
    key: 'academy',
    pad: '/opleider/academy',
    icon: GraduationCap,
    rollen: ALLE_ROLLEN,
    items: [
      { key: 'cursusInvoeren', pad: '/opleider/academy/cursus-invoeren', icon: PlusCircle, rollen: BEHEER, aliassen: ['/opleider/trainings'] },
      { key: 'trainingen', pad: '/opleider/academy/trainingen', icon: Table2, rollen: ALLE_ROLLEN },
      { key: 'preview', pad: '/opleider/academy/preview', icon: MonitorPlay, rollen: BEHEER },
      { key: 'team', pad: '/opleider/academy/team', icon: UsersRound, rollen: EIGENAAR },
    ],
  },
  {
    key: 'data',
    pad: '/opleider/data',
    icon: BarChart3,
    rollen: BEHEER,
    items: [
      { key: 'analytics', pad: '/opleider/data/analytics', icon: PieChart, rollen: BEHEER },
      { key: 'reviews', pad: '/opleider/data/reviews', icon: Trophy, rollen: BEHEER },
      { key: 'feedback', pad: '/opleider/data/feedback', icon: Sparkles, rollen: BEHEER },
    ],
  },
  {
    key: 'finance',
    pad: '/opleider/finance',
    icon: Euro,
    // Hoofdmenu ook voor ADMINISTRATOR, maar die ziet alleen Helpdesk & Escalaties (klachtenreglement)
    rollen: BEHEER,
    items: [
      { key: 'stripe', pad: '/opleider/finance/stripe', icon: Landmark, rollen: EIGENAAR },
      { key: 'ledger', pad: '/opleider/finance/ledger', icon: Receipt, rollen: EIGENAAR },
      { key: 'fees', pad: '/opleider/finance/fees', icon: Percent, rollen: EIGENAAR },
      { key: 'helpdesk', pad: '/opleider/finance/helpdesk', icon: Scale, rollen: BEHEER },
    ],
  },
  {
    key: 'chats',
    pad: '/opleider/chats',
    icon: MessageSquare,
    rollen: ALLE_ROLLEN,
    items: [
      { key: 'cursistGesprekken', pad: '/opleider/chats/cursisten', icon: Smartphone, rollen: ALLE_ROLLEN },
      { key: 'zakelijkOverleg', pad: '/opleider/chats/zakelijk', icon: HeartHandshake, rollen: BEHEER },
    ],
  },
];

export function isOpleiderRol(v: unknown): v is OpleiderRol {
  return v === 'OWNER' || v === 'ADMINISTRATOR' || v === 'TRAINER';
}

export function magRol(rollen: OpleiderRol[], rol: OpleiderRol): boolean {
  return rollen.includes(rol);
}

/** Hoofdmenu's die deze rol mag zien (volgorde = topnav-volgorde). */
export function hoofdmenusVoor(rol: OpleiderRol): OpleiderHoofdmenu[] {
  return OPLEIDER_MENU.filter((m) => magRol(m.rollen, rol)).map((m) => ({ ...m, items: m.items.filter((i) => magRol(i.rollen, rol)) }));
}

/** Eerste pad dat deze rol mag openen (startscherm na login / na een geweigerde route). */
export function startPadVoor(rol: OpleiderRol): string {
  const eerste = hoofdmenusVoor(rol)[0];
  return eerste?.items[0]?.pad ?? '/opleider/academy/trainingen';
}

function pastOp(pad: string, pathname: string): boolean {
  return pathname === pad || pathname.startsWith(pad + '/');
}

export function subItemVoorPad(pathname: string): { menu: OpleiderHoofdmenu; item: OpleiderSubItem } | null {
  let beste: { menu: OpleiderHoofdmenu; item: OpleiderSubItem; lengte: number } | null = null;
  for (const menu of OPLEIDER_MENU) {
    for (const item of menu.items) {
      for (const p of [item.pad, ...(item.aliassen ?? [])]) {
        if (pastOp(p, pathname) && (!beste || p.length > beste.lengte)) beste = { menu, item, lengte: p.length };
      }
    }
  }
  return beste ? { menu: beste.menu, item: beste.item } : null;
}

export function hoofdmenuVoorPad(pathname: string): OpleiderHoofdmenu | null {
  return subItemVoorPad(pathname)?.menu ?? OPLEIDER_MENU.find((m) => pastOp(m.pad, pathname)) ?? null;
}

/** Eerste submenu-pad van een hoofdmenu voor deze rol (topnav-klik). */
export function hoofdmenuStartPad(menu: OpleiderHoofdmenu, rol: OpleiderRol): string {
  return menu.items.find((i) => magRol(i.rollen, rol))?.pad ?? menu.pad;
}
